import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { callOrderApi } from '@/store/orders/call/api/index.ts';
import { isCallOrder } from '@/store/orders/call/helpers.ts';
import type { CallOrder, CallOrderProperties, ValidateCallOrderRequest } from '@/store/orders/call/state.ts';
import { isError400WithPayload, type OrderById, type ValidateOrderResponse } from '@/store/orders/state.ts';

const mapProperties = (
  currentOrder: CallOrder,
  originalArgs: ValidateCallOrderRequest,
  payload?: ValidateOrderResponse,
): CallOrderProperties => {
  const currentTime = Date.now();
  const clockOffset =
    payload?.backendTime !== null && payload?.backendTime !== 0 && typeof payload?.backendTime === 'number'
      ? payload?.backendTime - currentTime
      : undefined;

  return {
    status: currentOrder.properties.status,
    isReadyToSubmit: Boolean(payload?.isReadyToSubmit),
    mode: currentOrder.properties.mode,
    clockOffset,

    currencyPair: {
      currency1: originalArgs.currency1,
      currency2: originalArgs.currency2,
      tiering: currentOrder.properties.currencyPair?.tiering,
    },

    limitPrice: originalArgs.limitPrice ?? payload?.limitPrice ?? undefined,

    expiry: originalArgs.expiry,

    notificationEmails: originalArgs.notificationEmails?.isOverridenEmails
      ? originalArgs.notificationEmails.emails
      : currentOrder.properties.notificationEmails,
  };
};

// ██████╗ ███████╗███╗   ██╗██████╗ ██╗███╗   ██╗ ██████╗
// ██╔══██╗██╔════╝████╗  ██║██╔══██╗██║████╗  ██║██╔════╝
// ██████╔╝█████╗  ██╔██╗ ██║██║  ██║██║██╔██╗ ██║██║  ███╗
// ██╔═══╝ ██╔══╝  ██║╚██╗██║██║  ██║██║██║╚██╗██║██║   ██║
// ██║     ███████╗██║ ╚████║██████╔╝██║██║ ╚████║╚██████╔╝
// ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═════╝ ╚═╝╚═╝  ╚═══╝ ╚═════╝

export const validatePending = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(isAnyOf(callOrderApi.endpoints.validateCallOrder.matchPending), (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;
    const currentOrder = state[tileId];
    if (isNotDefined(currentOrder) || !isCallOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,
      properties: {
        ...currentOrder.properties,
        limitPrice: action.meta.arg.originalArgs.limitPrice ?? undefined,
        expiry:
          'expiry' in action.meta.arg.originalArgs
            ? action.meta.arg.originalArgs.expiry
            : currentOrder.properties.expiry,
        notificationEmails:
          action.meta.arg.originalArgs.notificationEmails?.emails ?? currentOrder.properties.notificationEmails,
      },
    };

    return state;
  });

// ███████╗██╗   ██╗██╗     ██╗     ███████╗██╗██╗     ██╗     ███████╗██████╗
// ██╔════╝██║   ██║██║     ██║     ██╔════╝██║██║     ██║     ██╔════╝██╔══██╗
// █████╗  ██║   ██║██║     ██║     █████╗  ██║██║     ██║     █████╗  ██║  ██║
// ██╔══╝  ██║   ██║██║     ██║     ██╔══╝  ██║██║     ██║     ██╔══╝  ██║  ██║
// ██║     ╚██████╔╝███████╗███████╗██║     ██║███████╗███████╗███████╗██████╔╝
// ╚═╝      ╚═════╝ ╚══════╝╚══════╝╚═╝     ╚═╝╚══════╝╚══════╝╚══════╝╚═════╝

export const validateFulfilled = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(callOrderApi.endpoints.validateCallOrder.matchFulfilled, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || !isCallOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(action.meta.arg.originalArgs.currency1) || isNotDefined(action.meta.arg.originalArgs.currency2)) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,
      type: 'CallOrder',
      clientId: currentOrder.clientId,
      properties: {
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, action.payload),
        errors: {},
      },
    };

    return state;
  });

// ██████╗ ███████╗     ██╗███████╗ ██████╗████████╗███████╗██████╗
// ██╔══██╗██╔════╝     ██║██╔════╝██╔════╝╚══██╔══╝██╔════╝██╔══██╗
// ██████╔╝█████╗       ██║█████╗  ██║        ██║   █████╗  ██║  ██║
// ██╔══██╗██╔══╝  ██   ██║██╔══╝  ██║        ██║   ██╔══╝  ██║  ██║
// ██║  ██║███████╗╚█████╔╝███████╗╚██████╗   ██║   ███████╗██████╔╝
// ╚═╝  ╚═╝╚══════╝ ╚════╝ ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝╚═════╝

export const validateRejected = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(callOrderApi.endpoints.validateCallOrder.matchRejected, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];
    const payload = isError400WithPayload(action.payload) ? action.payload : undefined;

    if (isNotDefined(currentOrder) || currentOrder.type !== 'CallOrder') {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(payload)) {
      state[tileId] = {
        ...currentOrder,
        type: 'CallOrder',
        properties: {
          ...currentOrder.properties,
          status: { type: 'failed', errorMessage: 'order.unexpected' },
        },
      } as CallOrder;
      return state;
    }

    const errors = isDefined(payload?.validationResponseDetailContent)
      ? payload.validationResponseDetailContent.reduce(
          (acc, error) => {
            if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
              acc.notional = error;
            }

            if (error.field === 'base64Emails') {
              acc.notificationEmails = error;
            }

            acc[error.field] = error;
            return acc;
          },
          {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
        )
      : undefined;

    state[tileId] = {
      ...currentOrder,
      type: 'CallOrder',
      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, payload),
        errors,
      },
    } as CallOrder;

    return state;
  });
